// The team, as one card on /account, beside the personal plan — seats are money, so they live with
// the rest of the money. A Team is the group of people whose agents are paid for together: one plan
// per org. Seats are BOUGHT (the owner picks how many of each kind — that is the bill), people are
// INVITED (owner or an admin, same org), and a seat is ASSIGNED to a person. Only seat-holders are
// covered; an unassigned seat waits for the next hire. Two kinds are sold: Standard and Premium
// cover a person's agent (Premium with 5x the allowance) and include team memory, for agent and
// plugin users alike. A Memory seat (shared spaces and capture alone) is no longer sold; it still
// shows, and can be counted down, on a team that bought one.
// Anyone in the org can start a team (they become its owner). Someone who also holds a personal
// plan is told so — nothing is cancelled automatically.
import { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Switch, TextField, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { addTeamMember, assignTeamSeat, createCheckoutSession, getTeamPlan, previewTeamSeats, removeTeamMember, setTeamRole, setTeamSeats, setTeamSettings } from "../utils/api";
import { ACCOUNT_PATH } from "../utils/accountRedirect";
import { resetLabel } from "../utils/resetTime";
import { C, CARD, MICRO_LABEL } from "./memoryTheme";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const SEAT_KINDS = ["standard", "premium", "memory"];             // the order they are shown in, everywhere
const SEAT_LABEL = { standard: "Standard", premium: "Premium", memory: "Memory" };
const SEAT_BLURB = { standard: "Agent or plugin, with team memory", premium: "Agent, 5\u00d7 the allowance", memory: "Shared spaces and capture, no agent" };
const DEFAULT_USD = { standard: 10, premium: 50, memory: 10 };     // shown only until the API answers
// One paid line per person: a seat OR their own plan, never both. Someone already on a paid personal
// plan needs nothing from the team to reach a shared space, so giving them a seat is the team taking
// over a bill they are already paying — fine, but it should be a decision, not an accident. Marked
// in the roster; the person's own double-pay notice sits below, next to the plan card that cancels it.
const paidOwnPlan = (plan) => Boolean(plan) && plan !== "free";
const EMPTY = { bought: 0, assigned: 0, free: 0 };
const sumBy = (obj, f) => SEAT_KINDS.reduce((acc, k) => acc + f(k, obj[k]), 0);
// The kinds on offer: Standard and Premium, plus Memory only where the team already holds some.
const offered = (seats) => SEAT_KINDS.filter((k) => k !== "memory" || ((seats || {})[k] || EMPTY).bought > 0);

const selectSx = {
  fontSize: 12.5, color: C.ink, bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: 1, px: 1, py: 0.5,
  fontFamily: "inherit", "&:disabled": { color: C.muted, bgcolor: "transparent", borderColor: "transparent" },
};

// One seat type the owner is deciding on: how many, at what price, how many are already in use.
function SeatStepper({ seat, price, blurb, value, min, onChange, disabled }) {
  const label = SEAT_LABEL[seat];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, color: C.ink, fontWeight: 600 }}>{label} · {money(price)}/mo</Typography>
        <Typography sx={{ fontSize: 12, color: C.muted }}>{blurb}</Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Button size="small" variant="outlined" disabled={disabled || value <= min} onClick={() => onChange(value - 1)}
                aria-label={`Remove a ${label} seat`} sx={{ minWidth: 32, px: 0, color: C.tealDeep, borderColor: C.line }}>−</Button>
        <Typography sx={{ width: 28, textAlign: "center", fontSize: 14, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums" }}
                    aria-label={`${label} seats`}>{value}</Typography>
        <Button size="small" variant="outlined" disabled={disabled} onClick={() => onChange(value + 1)}
                aria-label={`Add a ${label} seat`} sx={{ minWidth: 32, px: 0, color: C.tealDeep, borderColor: C.line }}>+</Button>
      </Box>
    </Box>
  );
}

// Who holds which seat. A seat type with nothing free can't be picked (unless it is the current one).
function SeatSelect({ who, value, seats, onChange, disabled }) {
  const free = (k) => (seats[k] || EMPTY).free;
  return (
    <Box component="select" value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}
         aria-label={`Seat for ${who}`} sx={selectSx}>
      <option value="">No seat</option>
      {offered(seats).map((k) => (
        <option key={k} value={k} disabled={value !== k && free(k) < 1}>
          {SEAT_LABEL[k]}{value !== k ? ` (${free(k)} free)` : ""}
        </option>
      ))}
    </Box>
  );
}

export default function TeamPlanCard() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");   // loading | ok | error
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const [inviteSeat, setInviteSeat] = useState("");
  const [draft, setDraft] = useState(null);         // {standard, premium, memory} while the owner edits the count
  const [preview, setPreview] = useState(null);     // Stripe's pricing of the pending change, shown before it is made

  const load = useCallback(async () => {
    try { setData(await getTeamPlan()); setState("ok"); }
    catch (e) { if (e?.message !== "REDIRECTING_FOR_AUTH") setState("error"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); await load(); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || "Something went wrong."); }
    finally { setBusy(false); }
  };

  if (state === "loading") {
    return (
      <Box sx={{ ...CARD, p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={16} sx={{ color: C.teal }} />
        <Typography sx={{ fontSize: 12.5, color: C.muted }}>Loading your team…</Typography>
      </Box>
    );
  }
  if (state === "error") {
    return <Box sx={{ ...CARD, p: 2 }}><Typography sx={{ fontSize: 12.5, color: C.muted }}>Team details are unavailable right now.</Typography></Box>;
  }

  const { billing = {}, active, exists, role, can_manage_people: canPeople, can_manage_money: canMoney, my_seat: mySeat,
          members = [], pending = [], seats = {}, seat_prices: prices = {}, personal_plan: personal, usage, usage_scope: scope, configured } = data;
  const seatOf = (k) => seats[k] || EMPTY;
  const priceOf = Object.fromEntries(SEAT_KINDS.map((k) => [k, Number(prices[`${k}_usd`] || DEFAULT_USD[k])]));
  const bought = Object.fromEntries(SEAT_KINDS.map((k) => [k, seatOf(k).bought]));
  const count = draft || bought;                                   // what the header prices
  const total = sumBy(count, (_, n) => n);
  const monthly = sumBy(count, (k, n) => n * priceOf[k]);
  const dirty = Boolean(draft) && SEAT_KINDS.some((k) => draft[k] !== bought[k]);
  const wanted = () => SEAT_KINDS.map((k) => count[k]);           // the API takes (standard, premium, memory)
  const editing = Boolean(draft) || (!active && canMoney);          // before checkout the steppers are always out
  const paidPersonal = personal && personal !== "free";
  const isOwner = role === "owner" || (!exists && canMoney);

  const startEditing = () => { setPreview(null); setDraft({ ...bought }); };
  const stopEditing = () => { setPreview(null); setDraft(null); };
  const review = () => run(async () => { setPreview(await previewTeamSeats(...wanted())); });
  const saveSeats = () => run(async () => { await setTeamSeats(...wanted()); setPreview(null); setDraft(null); });
  const cents = (c) => `$${(Math.abs(Number(c || 0)) / 100).toFixed(2)}`;
  const start = async () => {
    setBusy(true); setErr("");
    try {
      if (dirty) await setTeamSeats(...wanted());
      await createCheckoutSession(null, { product: "team", returnTo: ACCOUNT_PATH });
    } catch (e) { setErr(e?.response?.data?.error || e?.message || "Could not start checkout."); setBusy(false); }
  };
  const invite = () => {
    const em = email.trim(); if (!em) return;
    run(async () => { await addTeamMember(em, inviteSeat || null); setEmail(""); setInviteSeat(""); });
  };

  const title = active
    ? `${plural(total, "seat")} · ${money(monthly)}/mo`
    : exists && total > 0 ? `${plural(total, "seat")} planned · ${money(monthly)}/mo` : exists ? "Team, no seats yet" : "No team yet";

  return (
    <Box sx={{ ...CARD, p: 2.25 }} data-team-card="">
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={MICRO_LABEL}>Team</Typography>
          <Typography sx={{ color: C.ink, fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
            {title}
            {active && billing.cancel_at_period_end && <Box component="span" sx={{ ml: 1, fontSize: 12.5, fontWeight: 500, color: C.muted }}>ends this period</Box>}
          </Typography>
        </Box>
        {!active && canMoney && (
          <Button variant="contained" onClick={start} disabled={busy || !configured || total < 1}
                  sx={{ bgcolor: C.teal, textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: C.tealDeep } }}>
            {busy ? "Opening checkout…" : total < 1 ? "Start team plan" : `Start team plan · ${plural(total, "seat")} · ${money(monthly)}/mo`}
          </Button>
        )}
        {active && canMoney && !draft && (
          <Button variant="outlined" size="small" onClick={startEditing} disabled={busy}
                  sx={{ textTransform: "none", color: C.tealDeep, borderColor: C.teal }}>Change seats</Button>
        )}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.75, lineHeight: 1.6 }}>
        Buy seats, then give each one to a person. Agent usage is pooled across the team.
        {active && usage ? ` Pool ${usage.pool_used_pct}% used this week${scope ? ` (${scope})` : ""}${resetLabel(usage) ? ` · resets ${resetLabel(usage)}` : ""}.` : ""}
      </Typography>
      {/* Only the MEMORY seat's version of paying twice lives here. The agent-seat one belongs to the
          plan card above, which knows both amounts and owns the cancel button; a memory seat is not an
          agent entitlement, so that card never sees it. */}
      {active && mySeat === "memory" && paidPersonal && (
        <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, bgcolor: "#fff8ec", border: "1px solid #f0dca8",
                   borderLeft: "3px solid #d9a93a" }} role="status">
          <Typography sx={{ fontSize: 12.5, color: "#7a5d10", lineHeight: 1.6 }}>
            <b>Paid for twice.</b> Your own {personal} plan already reaches this team's shared spaces, so the
            team is paying {money(priceOf.memory)} a month for a Memory seat you do not need. Ask its owner to
            take the seat back — it frees the seat, and nothing about your memory changes.
          </Typography>
        </Box>
      )}

      {/* Seats: how many of each. The owner's call — the bill. */}
      {canMoney && editing && (
        <Box sx={{ mt: 1.5, borderTop: `1px solid ${C.line}` }}>
          {SEAT_KINDS.filter((k) => k !== "memory" || seatOf(k).bought > 0).map((k) => (
            <SeatStepper key={k} seat={k} price={priceOf[k]} value={count[k]} min={seatOf(k).assigned} disabled={busy}
                         blurb={`${SEAT_BLURB[k]} · ${seatOf(k).assigned} assigned, ${Math.max(0, count[k] - seatOf(k).assigned)} free`}
                         onChange={(v) => { setPreview(null); setDraft({ ...count, [k]: v }); }} />
          ))}
          {active && draft && !preview && (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", pb: 1 }}>
              <Button size="small" onClick={stopEditing} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>Cancel</Button>
              <Button size="small" variant="contained" onClick={review} disabled={busy || !dirty}
                      sx={{ bgcolor: C.teal, textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: C.tealDeep } }}>
                Review change
              </Button>
            </Box>
          )}
          {active && draft && preview && (
            <Box sx={{ mb: 1, p: 1.5, borderRadius: 1.5, bgcolor: C.tealSoft, border: `1px solid ${C.teal}` }} role="status">
              <Typography sx={{ fontSize: 13.5, color: C.ink, fontWeight: 600 }}>
                Your bill goes from {money(preview.monthly_now_usd)}/mo to {money(preview.monthly_after_usd)}/mo.
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: C.body, mt: 0.5 }}>
                {preview.preview_error
                  ? "Stripe couldn't price the rest of this period just now; the change is still prorated to the day."
                  : preview.due_today_cents == null || preview.due_today_cents === 0
                    ? "Nothing changes on today's bill."
                    : preview.due_today_cents > 0
                      ? `${cents(preview.due_today_cents)} is charged now for the rest of this period.`
                      : `${cents(preview.due_today_cents)} is credited to your next invoice for the rest of this period.`}
                {" "}Seats already assigned stay assigned.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                <Button size="small" onClick={() => setPreview(null)} disabled={busy} sx={{ textTransform: "none", color: C.muted }}>Back</Button>
                <Button size="small" variant="contained" onClick={saveSeats} disabled={busy}
                        sx={{ bgcolor: C.teal, textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: C.tealDeep } }}>
                  Confirm · {money(preview.monthly_after_usd)}/mo
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {active && canMoney && !draft && (
        <Typography sx={{ fontSize: 12.5, color: C.body, mt: 1.25 }}>
          {SEAT_KINDS.filter((k) => seatOf(k).bought > 0 || k !== "memory")   // Memory shows once it is bought
                     .map((k) => `${plural(seatOf(k).bought, `${SEAT_LABEL[k]} seat`)} (${seatOf(k).free} free)`).join(" · ")}
        </Typography>
      )}

      {/* People: who is on the team, their role, and which seat they hold. */}
      <Box sx={{ mt: 1.5, borderTop: `1px solid ${C.line}` }}>
        {members.map((m) => {
          const who = m.email || m.uid;
          const canRemove = canPeople && !m.is_owner && (isOwner || m.role !== "admin");
          return (
            <Box key={m.uid} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: `1px dashed ${C.line}` }}>
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: C.ink, fontWeight: 500 }} noWrap>
                {who}
                {paidOwnPlan(m.personal_plan) && m.seat !== "standard" && m.seat !== "premium" && (
                  <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                                              borderRadius: "999px", px: 1, py: "1px",
                                              color: m.seat === "memory" ? "#7a5d10" : C.muted,
                                              bgcolor: m.seat === "memory" ? "#fff8ec" : C.line }}
                       title={m.seat === "memory"
                         ? `Their own ${m.personal_plan} plan already reaches the shared spaces, so this Memory seat is a second bill for the same thing. Take it back to free the seat.`
                         : "They pay for their own plan, which already reaches the shared spaces. A seat here would move that bill to the team."}>
                    {m.seat === "memory" ? `Memory seat not needed · own ${m.personal_plan} plan` : `own ${m.personal_plan} plan`}
                  </Box>
                )}
                {m.role === "owner" && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: C.tealDeep, bgcolor: C.tealSoft, borderRadius: "999px", px: 1, py: "1px" }}>Owner</Box>}
                {m.role === "admin" && <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: C.tealDeep, bgcolor: C.tealSoft, borderRadius: "999px", px: 1, py: "1px" }}>Admin</Box>}
              </Typography>
              {isOwner && !m.is_owner && (
                <Button size="small" disabled={busy} onClick={() => run(() => setTeamRole(m.uid, m.role === "admin" ? "member" : "admin"))}
                        sx={{ textTransform: "none", minWidth: 0, fontSize: 12, color: C.muted }}>
                  {m.role === "admin" ? "Remove admin" : "Make admin"}
                </Button>
              )}
              {canPeople ? (
                <SeatSelect who={who} value={m.seat} seats={seats} disabled={busy} onChange={(seat) => run(() => assignTeamSeat(m.uid, seat))} />
              ) : (
                <Typography sx={{ fontSize: 12, color: m.seat ? C.tealDeep : C.muted, fontWeight: m.seat === "premium" ? 700 : 500 }}>
                  {m.seat ? SEAT_LABEL[m.seat] : "No seat"}
                </Typography>
              )}
              {canRemove && (
                <Tooltip title="Remove from team"><IconButton size="small" disabled={busy} aria-label={`Remove ${who}`}
                  onClick={() => run(() => removeTeamMember(m.uid))}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              )}
            </Box>
          );
        })}
        {pending.map((p) => (
          <Box key={p.email} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: `1px dashed ${C.line}` }}>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13.5, color: C.muted }} noWrap>{p.email} · invited, hasn't signed up yet</Typography>
            {canPeople ? (
              <SeatSelect who={p.email} value={p.seat} seats={seats} disabled={busy} onChange={(seat) => run(() => assignTeamSeat(p.email, seat))} />
            ) : (
              <Typography sx={{ fontSize: 12, color: C.muted }}>{p.seat ? SEAT_LABEL[p.seat] : "No seat"}</Typography>
            )}
            {canPeople && (
              <Tooltip title="Withdraw invite"><IconButton size="small" disabled={busy} aria-label={`Withdraw ${p.email}`}
                onClick={() => run(() => removeTeamMember(p.email))}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            )}
          </Box>
        ))}
      </Box>

      {canPeople ? (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <TextField size="small" placeholder="teammate@yourcompany.com" value={email} sx={{ flex: 1, minWidth: 200 }}
                     onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") invite(); }}
                     inputProps={{ "aria-label": "Teammate email" }} />
          <Box component="select" value={inviteSeat} onChange={(e) => setInviteSeat(e.target.value)} aria-label="Seat for the invite" sx={selectSx}>
            <option value="">No seat yet</option>
            {SEAT_KINDS.filter((k) => k !== "memory" || seatOf(k).bought > 0).map((k) => (
              <option key={k} value={k} disabled={seatOf(k).free < 1}>{SEAT_LABEL[k]} ({seatOf(k).free} free)</option>
            ))}
          </Box>
          <Button variant="outlined" size="small" disabled={busy || !email.trim()} onClick={invite}
                  sx={{ textTransform: "none", whiteSpace: "nowrap", color: C.tealDeep, borderColor: C.teal }}>
            Invite
          </Button>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 1.25 }}>
          {mySeat ? `Your seat: ${SEAT_LABEL[mySeat]}.` : "You're on the team without a seat — ask the owner or an admin for one."}
          {" "}Only the team's owner or an admin can invite people and assign seats.
        </Typography>
      )}
      {/* Sharing outside the company. Off by default: a company chooses to let its memory leave its
          domain. Coworkers in shared spaces still need a seat either way. An older backend sends no
          field, and the row isn't shown. */}
      {active && typeof data.allow_external_guests === "boolean" && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px dashed ${C.line}`, display: "flex", alignItems: "flex-start", gap: 1.5 }} data-testid="external-guests">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: C.ink }}>Guests from outside your company</Typography>
            <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25, lineHeight: 1.55 }}>
              {data.allow_external_guests
                ? "On: people with any email can be invited to your team's shared spaces. They're marked Guest, and their chats run on their own plan, not a seat."
                : "Off: your team's shared spaces can be shared only with people at your company."}
              {!canPeople && " Your team's owner or an admin can change this."}
            </Typography>
          </Box>
          {canPeople && (
            <Switch checked={data.allow_external_guests} disabled={busy}
                    onChange={(e) => run(() => setTeamSettings({ allow_external_guests: e.target.checked }))}
                    inputProps={{ "aria-label": "Allow guests from outside your company" }}
                    sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: C.teal }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: C.teal } }} />
          )}
        </Box>
      )}
      {err && <Typography sx={{ fontSize: 12.5, color: "#b42318", mt: 1 }}>{err}</Typography>}
    </Box>
  );
}
